import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Fade from '@mui/material/Fade';
import { setToken, setUser, setApiBase, API_BASE_URL, HOME_API_BASE_URL } from './auth';

function Login({ onLogin, notice }) {
  const logoSrc = process.env.PUBLIC_URL + '/heybro-logo.png';
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const login = (base) => fetch(`${base}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      // Every login starts at this build's own backend. It may hand the user
      // to another instance ({login_at}: prod sends Marcuard Heritage users to
      // the dev instance) — then that instance checks the password and the
      // whole session talks to it directly.
      let base = HOME_API_BASE_URL;
      let response = await login(base);
      let data = await response.json();
      if (response.ok && data.login_at) {
        base = String(data.login_at).replace(/\/+$/, '');
        response = await login(base);
        data = await response.json();
      }

      if (response.ok && data.token) {
        setToken(data.token);
        setUser(data.user);
        setApiBase(base);
        // Modules build their API URLs from API_BASE_URL when they load, so a
        // session on a different backend than the page was loaded for needs
        // one reload to pick it up.
        if (base !== API_BASE_URL) {
          window.location.reload();
          return;
        }
        onLogin(true, data.user);
      } else {
        onLogin(false);
        setError(data.error || 'Falscher Benutzername oder Passwort.');
      }
    } catch (err) {
      onLogin(false);
      setError('Verbindungsfehler. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fade in timeout={600}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '80vh',
          width: '100%',
          maxWidth: 360,
          mx: 'auto',
          px: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 4 }}>
          <Box
            component="img"
            src={logoSrc}
            alt="HeyBro Finance"
            sx={{
              height: 66,
              width: 'auto',
              filter: 'drop-shadow(0 4px 16px rgba(0, 0, 0, 0.35))',
            }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography
              sx={{
                fontFamily: "'Outfit', sans-serif",
                fontWeight: 800,
                fontSize: { xs: '30px', md: '34px' },
                lineHeight: 1,
                color: '#ffffff',
              }}
            >
              Hey<Box component="span" sx={{ color: '#029A3B' }}>Bro</Box>
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Outfit', sans-serif",
                fontWeight: 500,
                fontSize: { xs: '11px', md: '13px' },
                letterSpacing: '0.5em',
                lineHeight: 1,
                color: '#ffffff',
                mt: '6px',
                ml: '2px',
              }}
            >
              FINANCE
            </Typography>
          </Box>
        </Box>

        <Box sx={{ width: '100%', borderTop: 1, borderColor: 'divider', pt: 3, mb: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 300,
              mb: 3,
              textAlign: 'center',
              color: 'text.secondary',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontSize: '14px',
            }}
          >
            Login
          </Typography>
        </Box>

        {notice ? (
          <Typography variant="body2" sx={{ width: '100%', mb: 2, textAlign: 'center', color: 'warning.main' }}>
            {notice}
          </Typography>
        ) : null}

        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
          <TextField
            fullWidth
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Benutzername"
            variant="outlined"
            size="small"
            sx={{ mb: 1.5 }}
          />
          <TextField
            fullWidth
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Passwort"
            variant="outlined"
            size="small"
            sx={{ mb: 1 }}
          />

          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mb: 3,
              cursor: 'pointer',
              '&:hover': { color: 'primary.main' },
            }}
          >
            Passwort vergessen?
          </Typography>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{ height: 44, fontSize: '15px', fontWeight: 600 }}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : 'Einloggen'}
          </Button>

          {error && (
            <Typography variant="body2" color="error" sx={{ mt: 2, textAlign: 'center' }}>
              {error}
            </Typography>
          )}

          <Typography
            variant="body2"
            sx={{
              mt: 5,
              textAlign: 'center',
              color: 'text.primary',
              cursor: 'pointer',
              '&:hover': { color: 'primary.main' },
            }}
          >
            Neu? Jetzt registrieren!
          </Typography>
        </Box>
      </Box>
    </Fade>
  );
}

export default Login;
